interface PeopleAudience {
    name: string;
    audienceType: string;
}

interface GalleryPicture {
    name: string;
    title: string;
    siteTitle?: string;
    alt: string;
    aria_label: string;
    srcSet: { avif: string; webp: string; source: string; fallbackSrc: string };
    sizes: string;
    width: number;
    height: number;
    htmlDescription?: string;
    description?: string;
    audience?: PeopleAudience[] | null;
    duration?: string | null;
    relatedPatterns?: string[];
    ctaUrl: string;
    // Komunikat z nazwą usługi jako linkiem (before + link + after).
    ctaMessage: { before: string; link: string; after: string };
    ctaPrice: string;
    ctaLabel: string;
    route: string;
    otherRoute: string;
    // null = za mało odsłon, licznik ukryty (GalleryPictures.MIN_VISIBLE_VISITS).
    pictureVisited: number | null;
}

// Miniatury "powiązanych wzorów": 2 kolumny, od md 3 (row-cols-2 row-cols-md-3).
const RELATED_THUMB_SIZES = '(min-width: 768px) 33vw, 50vw';

interface GalleryData {
    pictures: GalleryPicture[];
    currentPicture: string;
    webGalleryRoute: string;
}

(() => {
    // The bundle runs on every page, but #gallery-data exists only on gallery
    // picture pages: elsewhere there is nothing to do. On a picture page an
    // empty element is a rendering bug and still throws.
    const dataElement = document.getElementById('gallery-data');
    if (!dataElement) return;
    if (!dataElement.textContent) {
        throw new Error('Gallery data element (#gallery-data) is empty.');
    }

    const data: GalleryData = JSON.parse(dataElement.textContent);
    const pictures: GalleryPicture[] = data.pictures;

    let currentIndex: number = pictures.findIndex((p) => p.name === data.currentPicture);

    /**
     * Sekcja ze strony albo — gdy zdjęcie ma dane, a serwer jej nie wyrenderował
     * (poprzednie zdjęcie ich nie miało) — utworzona z <template id="…Template">.
     */
    function getSection(id: string, needed: boolean): HTMLElement | null {
        const existing = document.getElementById(id);
        if (existing || !needed) return existing;
        const template = document.getElementById(`${id}Template`) as HTMLTemplateElement | null;
        const section = template?.content.firstElementChild?.cloneNode(true) as HTMLElement | undefined;
        if (!template || !section) return null;
        template.before(section);
        return section;
    }

    /** Poprzedni/Następny: prawdziwy link do sąsiada albo niewidoczny przycisk na końcu galerii. */
    function setStep(button: HTMLElement | null, target: GalleryPicture | undefined): void {
        if (!(button instanceof HTMLAnchorElement)) return;
        button.classList.toggle('invisible', !target);
        if (target) {
            button.href = target.route;
            button.removeAttribute('aria-hidden');
            button.removeAttribute('tabindex');
        } else {
            button.removeAttribute('href');
            button.setAttribute('aria-hidden', 'true');
            button.tabIndex = -1;
        }
    }

    function updateUI(): void {
        const currentPicture: GalleryPicture = pictures[currentIndex];

        // BACK LINK
        const backBtn = document.getElementById('backBtn') as HTMLAnchorElement | null;
        if (backBtn) {
            backBtn.href = `${data.webGalleryRoute}#${currentPicture.name}/`;
        }

        // VISIT COUNTER
        const visitCounter = document.getElementById('visitCounter');
        if (visitCounter) {
            visitCounter.innerHTML =
                currentPicture.pictureVisited === null
                    ? ''
                    : `
        <span class="badge bg-secondary">
          👁️ <i class="bi bi-eye"></i> ${currentPicture.pictureVisited} 👁️
        </span>
      `;
        }

        // IMAGE
        const pictureElement = document.querySelector<HTMLElement>('#mainFigure picture');
        if (pictureElement) {
            const { srcSet, sizes } = currentPicture;
            pictureElement.innerHTML = `
        <source srcset="${srcSet.avif}" sizes="${sizes}" type="image/avif">
        <source srcset="${srcSet.webp}" sizes="${sizes}" type="image/webp">
        <img
          class="gallery-stage__img rounded img-thumbnail"
          decoding="async"
          src="${srcSet.fallbackSrc}"
          srcset="${srcSet.source}"
          sizes="${sizes}"
          width="${currentPicture.width}"
          height="${currentPicture.height}"
          alt="${currentPicture.alt}"
          aria-label="${currentPicture.aria_label}"
        >
      `;
        }

        // TITLE
        const displayTitle = currentPicture.siteTitle || currentPicture.title;
        const titleElement = document.getElementById('pictureTitle');
        if (titleElement) titleElement.textContent = displayTitle;
        document.title = `${displayTitle} (${currentIndex + 1}/${pictures.length})`;

        // AUDIENCE
        const audienceSection = getSection('audienceSection', Boolean(currentPicture.audience?.length));
        if (audienceSection) {
            const badges = audienceSection.querySelector<HTMLElement>('.d-flex');
            if (currentPicture.audience && currentPicture.audience.length && badges) {
                badges.innerHTML = currentPicture.audience
                    .map((a) => `<span class="badge rounded-pill text-bg-light border">${a.name}</span>`)
                    .join('');
                audienceSection.classList.remove('d-none');
            } else {
                if (badges) badges.innerHTML = '';
                audienceSection.classList.add('d-none');
            }
        }

        // DURATION
        const durationSection = getSection('durationSection', Boolean(currentPicture.duration));
        if (durationSection) {
            const durationText = durationSection.querySelector('p');
            if (currentPicture.duration && durationText) {
                durationText.textContent = currentPicture.duration;
                durationSection.classList.remove('d-none');
            } else {
                durationSection.classList.add('d-none');
            }
        }

        // CTA
        // Komunikat, cena i przycisk zależą od usługi (grupy) zdjęcia.
        const ctaMessageEl = document.getElementById('ctaMessage');
        const ctaPriceEl = document.getElementById('ctaPrice');
        const ctaLink = document.getElementById('serviceCta') as HTMLAnchorElement | null;
        if (ctaMessageEl) {
            const { before, link, after } = currentPicture.ctaMessage;
            const serviceLink = document.createElement('a');
            serviceLink.id = 'ctaServiceLink';
            serviceLink.href = currentPicture.ctaUrl;
            serviceLink.textContent = link;
            ctaMessageEl.replaceChildren(before, serviceLink, after);
        }
        if (ctaPriceEl) ctaPriceEl.textContent = currentPicture.ctaPrice;
        if (ctaLink) {
            ctaLink.href = currentPicture.ctaUrl;
            ctaLink.textContent = currentPicture.ctaLabel;
        }

        // RELATED DESIGNS
        const relatedPics = (currentPicture.relatedPatterns || [])
            .map((relatedId) => pictures.find((p) => p.name === relatedId))
            .filter((p): p is GalleryPicture => Boolean(p));
        const relatedSection = getSection('relatedSection', relatedPics.length > 0);
        if (relatedSection) {
            const relatedRow = relatedSection.querySelector<HTMLElement>('.row');

            if (relatedPics.length && relatedRow) {
                relatedRow.innerHTML = relatedPics
                    .map(
                        (rp) => `
            <div class="col">
              <a href="${rp.route}" class="text-decoration-none">
                <picture>
                  <source srcset="${rp.srcSet.avif}" sizes="${RELATED_THUMB_SIZES}" type="image/avif">
                  <source srcset="${rp.srcSet.webp}" sizes="${RELATED_THUMB_SIZES}" type="image/webp">
                  <img src="${rp.srcSet.fallbackSrc}" srcset="${rp.srcSet.source}" sizes="${RELATED_THUMB_SIZES}" class="img-fluid w-100 rounded img-thumbnail mb-1" width="${rp.width}" height="${rp.height}" loading="lazy" decoding="async" alt="${rp.alt}">
                </picture>
                <div class="small text-body text-center">${rp.siteTitle || rp.title}</div>
              </a>
            </div>
          `,
                    )
                    .join('');
                relatedSection.classList.remove('d-none');
            } else {
                relatedSection.classList.add('d-none');
            }
        }

        // DESCRIPTION
        const figcaption = document.querySelector<HTMLElement>('#mainFigure figcaption');
        if (figcaption) {
            figcaption.innerHTML = currentPicture.htmlDescription || currentPicture.description || '';
        }

        // BUTTONS
        const prevBtn = document.getElementById('prevBtn');
        const nextBtn = document.getElementById('nextBtn');

        setStep(prevBtn, pictures[currentIndex - 1]);
        setStep(nextBtn, pictures[currentIndex + 1]);

        // LANGUAGE SWITCH
        const langSwitch = document.getElementById('language-switcher-id') as HTMLAnchorElement | null;
        if (langSwitch) langSwitch.href = currentPicture.otherRoute;

        // URL
        history.replaceState(null, '', currentPicture.route);
    }

    document.addEventListener('DOMContentLoaded', () => {
        updateUI();

        document.getElementById('prevBtn')?.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();
            if (currentIndex > 0) {
                currentIndex--;
                updateUI();
            }
        });

        document.getElementById('nextBtn')?.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();
            if (currentIndex < pictures.length - 1) {
                currentIndex++;
                updateUI();
            }
        });

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' && currentIndex > 0) {
                currentIndex--;
                updateUI();
            }
            if (e.key === 'ArrowRight' && currentIndex < pictures.length - 1) {
                currentIndex++;
                updateUI();
            }
        });
    });
})();
